import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { QIFlashScreen, QISpinner } from "../../components";
import { NativeSvgIcon } from "./Shared/";
import { CommonHeader } from "./Shared";
import {
  useGetLanguagesQuery,
  useGetLocalizationsQuery,
  useGetContainersQuery,
  useGetPluginsQuery,
} from "./services";
import { getPreferredLanguage, tHelper } from "../../shared/translation";
import { English, Bahasa, Malay, Chinese } from "../../lang";
import selectedLanguage from "../../shared/selectedLanguage.json";
import { FMDPStorage } from "../../shared/helper";
import { useDispatch, useSelector } from "react-redux";
import { useSelectClientAppMutation } from "../Authentication/service";
import { setlastVisitedApp } from "../../reduxStore/features";
import { getLocalizedString } from "../../shared/translation";
//Toast Message
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BytesizeIcon from "./Shared/BytesizeIcon";

export const MPLayout = ({ children, ...props }) => {
  const [selectedAppId, setLayoutSelectedAppId] = useState(FMDPStorage.get("selected-app-id"));
  const [appToToggle, setAppToToggle] = useState(selectedAppId);
  const [switchClicked, setSwitchClicked] = useState(null); // Changed to store app type instead of boolean
  const flashScreenStatus = useSelector((state) => state.common.flash_screen);
  const [showDefaultLangMsg, setShowDefaultLangMsg] = useState(false);

  const dispatch = useDispatch();
  const [currentUserLanguage] = useState(getPreferredLanguage());

  const isReseller = FMDPStorage?.get("reseller");

  const [
    selectClientApp,
    {
      isSuccess: selectClientAppSuccess,
      data: clientAppData,
      reset: resetSelectClientApp,
      error: selectClientAppError,
    },
  ] = useSelectClientAppMutation({
    fixedCacheKey: "shared-client-app-data",
  });

  const {
    data: pluginData,
    isFetching,
    isSuccess,
    error,
  } = useGetPluginsQuery({
    page: 0,
    per_page: 500,
  });

  const currentUser = FMDPStorage?.get("current-user")?.client_apps;

  const iDforClient = currentUser?.find((user, id) => {
    return user.id === selectedAppId;
  })?.client?.id;

  const filteredClients = currentUser?.filter((user) => {
    return user?.client?.id === iDforClient;
  });

  // Get the user config json and the localization profile
  let userConfigJson = "";
  let LocalizationProfile = "";
  filteredClients?.map((user) => {
    if (user?.id === selectedAppId) {
      userConfigJson = user?.client?.config_json;
    }
  });

  try {
    LocalizationProfile = JSON.parse(userConfigJson)?.tracking?.web?.localization_profile;
  } catch {}

  useEffect(() => {
    if (switchClicked) {
      try {
        if (filteredClients.length > 1) {
          let targetApp = null;

          // Find the specific app based on switchClicked value
          if (switchClicked === "tracking") {
            targetApp = filteredClients?.find((app) => app.name?.endsWith("- Tracking"));
          } else if (switchClicked === "eco-drive") {
            targetApp = filteredClients?.find((app) => app.name?.endsWith("- Eco-Drive"));
          } else {
            // Fallback to old behavior - find first non-current app
            const toggleId = filteredClients?.filter((item) => {
              return item.id !== selectedAppId;
            });
            targetApp = toggleId[0];
          }

          if (targetApp && targetApp.id !== appToToggle) {
            setAppToToggle(targetApp.id);
            selectClientApp({ client_app_id: targetApp.id });
          }
        }
      } catch (e) {
        console.log(e);
      }
      // Reset switchClicked after processing
      setSwitchClicked(null);
    }
  }, [selectedAppId, currentUser, switchClicked, filteredClients, appToToggle]);

  const profile = FMDPStorage.get("localization-profile") || "";

  const {
    data: localizationData,
    isSuccess: isLocalizationSuccess,
    isLoading: isLocalizationLoading,
    error: localizationError,
  } = useGetLocalizationsQuery({
    language: currentUserLanguage,
    profile: profile || LocalizationProfile || "Tracksynq Web",
    per_page: 1000,
  });

  const [transformedData, setTransformedData] = useState(null);

  useEffect(() => {
    const result = {};
    if (localizationData?.data) {
      localizationData?.data?.forEach((item) => {
        result[item.key] = item.value;
      });
      const translations = JSON.stringify(result);
      tHelper.setLocalizations(translations);
      setTransformedData(translations);
    }
  }, [localizationData]);

  useEffect(() => {
    if (currentUserLanguage && localizationData?.data?.length === 0) {
      setShowDefaultLangMsg(true);
    } else {
      setShowDefaultLangMsg(false);
    }
  }, [currentUserLanguage, localizationData]);

  const { data: languages, isSuccess: languageSuccess } = useGetLanguagesQuery({
    per_page: 1000,
    order_by: "name",
    application_variant_id: 1,
  });

  useEffect(() => {
    switch (currentUserLanguage) {
      case "en":
        selectedLanguage.Strings = English;
        break;
      case "id":
        selectedLanguage.Strings = Bahasa;
        break;
      case "ms":
        selectedLanguage.Strings = Malay;
        break;
      case "zh-TW":
        selectedLanguage.Strings = Chinese;
        break;
      default:
        console.log(
          "The application will use English as the following locale not supported: ",
          currentUserLanguage
        );
        selectedLanguage.Strings = English;
        break;
    }
  }, [currentUserLanguage]);

  const { data: newContainers } = useGetContainersQuery({ page: 0, per_page: 500 });

  const mpcontainers = newContainers?.data?.filter((container) => {
    if (!container?.system_default) {
      return container?.client_app_names.includes("Management Portal");
    }
  });

  const MPEntity = useMemo(() => {
    const baseEntities = isReseller
      ? [
          {
            label: getLocalizedString("dashboard", "Dashboard"),
            path: "dashboard",
            icon: "dashboard",
          },
          {
            label: getLocalizedString("devices", "Devices"),
            path: "devices",
            icon: "device",
            subNavigation: [
              { label: getLocalizedString("devices", "Devices"), path: "devices" },
              { label: getLocalizedString("models", "Models"), path: "devices/models" },
            ],
          },
          {
            label: getLocalizedString("vehicles", "Vehicles"),
            path: "vehicles",
            icon: "vehicle",
            subNavigation: [
              { label: getLocalizedString("vehicles", "Vehicles"), path: "vehicles" },
              {
                label: getLocalizedString("specifications", "Specifications"),
                path: "vehicles/specifications",
              },
              { label: getLocalizedString("variants", "Variants"), path: "vehicles/variants" },
              {
                label: getLocalizedString("models", "Models"),
                path: "vehicles/manufacturers_models",
              },
              {
                label: getLocalizedString("manufacturers", "Manufacturers"),
                path: "vehicles/manufacturers",
              },
              { label: getLocalizedString("classes", "Classes"), path: "vehicles/classes" },
              { label: getLocalizedString("types", "Types"), path: "vehicles/types" },
            ],
          },
          {
            label: getLocalizedString("simcards", "Simcards"),
            path: "sim_cards",
            icon: "simcard",
          },
          {
            label: getLocalizedString("groups", "Groups"),
            path: "groups",
            icon: "group",
            subNavigation: [
              { label: getLocalizedString("vehicles", "Vehicles"), path: "groups/vehicle" },
              { label: getLocalizedString("devices", "Devices"), path: "groups/device" },
              { label: getLocalizedString("users", "Users"), path: "groups/user" },
            ],
          },
          {
            label: getLocalizedString("configurations", "Configurations"),
            path: "configurations",
            icon: "undefined",
            subNavigation: [
              {
                label: getLocalizedString("backends", "Backends"),
                path: "configurations/backends",
              },
              {
                label: getLocalizedString("geofences", "Geofences"),
                path: "configurations/geofence",
              },
              {
                label: getLocalizedString("events", "Events"),
                path: "configurations/events",
              },
              {
                label: getLocalizedString("event_plans", "Event Plans"),
                path: "configurations/event_plans",
              },
              {
                label: getLocalizedString("trips", "Trips"),
                path: "configurations/trips",
              },
              {
                label: getLocalizedString("reports", "Reports"),
                path: "configurations/report",
              },
              {
                label: getLocalizedString("custom_fields", "Custom Fields"),
                path: "configurations/custom_fields",
              },
            ],
          },
          {
            label: getLocalizedString("clients", "Clients"),
            path: "clients",
            icon: "client",
          },
          {
            label: getLocalizedString("users_and_roles", "Users And Roles"),
            path: "users_roles",
            icon: "user",
            subNavigation: [
              { label: getLocalizedString("users", "Users"), path: "users_roles/users" },
              { label: getLocalizedString("roles", "Roles"), path: "users_roles/roles" },
              {
                label: getLocalizedString("data_access", "Data Access"),
                path: "users_roles/data_access",
              },
            ],
          },
          {
            label: getLocalizedString("audit_trail", "Audit Trail"),
            path: "audit-trail",
            icon: "audit-log",
          },
          {
            label: "Reports",
            path: "reports",
            icon: "reports",
          },
        ]
      : [
          {
            label: getLocalizedString("dashboard", "Dashboard"),
            path: "dashboard",
            icon: "dashboard",
          },
          {
            label: getLocalizedString("devices", "Devices"),
            path: "devices",
            icon: "device",
            subNavigation: [
              { label: getLocalizedString("devices", "Devices"), path: "devices" },
              { label: getLocalizedString("models", "Models"), path: "devices/models" },
            ],
          },
          {
            label: getLocalizedString("vehicles", "Vehicles"),
            path: "vehicles",
            icon: "vehicle",
            subNavigation: [
              { label: getLocalizedString("vehicles", "Vehicles"), path: "vehicles" },
              {
                label: getLocalizedString("specifications", "Specifications"),
                path: "vehicles/specifications",
              },
              { label: getLocalizedString("variants", "Variants"), path: "vehicles/variants" },
              {
                label: getLocalizedString("models", "Models"),
                path: "vehicles/manufacturers_models",
              },
              {
                label: getLocalizedString("manufacturers", "Manufacturers"),
                path: "vehicles/manufacturers",
              },
              { label: getLocalizedString("classes", "Classes"), path: "vehicles/classes" },
              { label: getLocalizedString("types", "Types"), path: "vehicles/types" },
            ],
          },
          {
            label: getLocalizedString("simcards", "Simcards"),
            path: "sim_cards",
            icon: "simcard",
          },
          {
            label: getLocalizedString("groups", "Groups"),
            path: "groups",
            icon: "group",
            subNavigation: [
              { label: getLocalizedString("vehicles", "Vehicles"), path: "groups/vehicle" },
              { label: getLocalizedString("devices", "Devices"), path: "groups/device" },
              { label: getLocalizedString("users", "Users"), path: "groups/user" },
            ],
          },
          {
            label: getLocalizedString("configurations", "Configurations"),
            path: "configurations",
            icon: "undefined",
            subNavigation: [
              {
                label: getLocalizedString("backends", "Backends"),
                path: "configurations/backends",
              },
              {
                label: getLocalizedString("geofences", "Geofences"),
                path: "configurations/geofence",
              },
              {
                label: getLocalizedString("events", "Events"),
                path: "configurations/events",
              },
              {
                label: getLocalizedString("event_plans", "Event Plans"),
                path: "configurations/event_plans",
              },
              {
                label: getLocalizedString("trips", "Trips"),
                path: "configurations/trips",
              },
              {
                label: getLocalizedString("reports", "Reports"),
                path: "configurations/report",
              },
              {
                label: getLocalizedString("custom_fields", "Custom Fields"),
                path: "configurations/custom_fields",
              },
            ],
          },
          {
            label: getLocalizedString("users_and_roles", "Users And Roles"),
            path: "users_roles",
            icon: "user",
            subNavigation: [
              { label: getLocalizedString("users", "Users"), path: "users_roles/users" },
              { label: getLocalizedString("roles", "Roles"), path: "users_roles/roles" },
              {
                label: getLocalizedString("data_access", "Data Access"),
                path: "users_roles/data_access",
              },
            ],
          },
          {
            label: getLocalizedString("audit_trail", "Audit Trail"),
            path: "audit-trail",
            icon: "audit-log",
          },
          /* {
            label: "Plugins",
            path: "plugin/:path",
            icon: "plugin",
          }, */
        ];

    const userAppFeatures = FMDPStorage.get("app_features") || {};
    const pluginFeaturePermissions = FMDPStorage.get("plugin_features");

    const newContainerEntities =
      mpcontainers
        ?.map((container) => {
          const enabledPlugins = pluginData?.data?.filter(
            (data) => data?.is_enabled && container.plugin_ids.includes(data.id)
          );

          if (!enabledPlugins?.length) {
            return;
          }

          // Check if user has permission to access at least one plugin in this container
          const hasAccess = enabledPlugins.some((plugin) => {
            // Source 1: Find sub-features registered in app_features (keys starting with "PluginName::")
            const pluginSubFeatureKeys = Object.keys(userAppFeatures).filter((key) =>
              key.startsWith(`${plugin.name}::`)
            );

            if (pluginSubFeatureKeys.length > 0) {
              // Plugin has registered sub-features in Source 1 — user needs read on at least one
              return pluginSubFeatureKeys.some(
                (key) => !!userAppFeatures[key]?.read
              );
            }

            // Source 2: Check plugin_feature_permissions from localStorage
            if (Array.isArray(pluginFeaturePermissions)) {
              const pluginFeatureId = plugin.plugin_features?.[0]?.id;
              if (pluginFeatureId) {
                const pfp = pluginFeaturePermissions.find(
                  (p) => p.plugin_feature_id === pluginFeatureId
                );
                if (pfp?.permission_json) {
                  return Object.values(pfp.permission_json).some(
                    (perms) => !!perms?.read
                  );
                }
              }
            }

            // No sub-features in either source — fallback to general "Plugins" permission
            return !!userAppFeatures["Plugins"]?.read;
          });

          if (!hasAccess) {
            return;
          }

          return {
            label: getLocalizedString(container.name, container.name),
            path: `container/${container.path}`,
            icon: container.icon,
          };
        })
        .filter(Boolean) || [];

    return [...baseEntities, ...newContainerEntities];
  }, [mpcontainers, transformedData, isReseller]);

  useEffect(() => {
    if (selectedAppId) {
      dispatch(setlastVisitedApp(selectedAppId));
    }
  }, [selectedAppId, dispatch]);

  return (
    <>
      <QIFlashScreen currentAppId={iDforClient} flashScreenStatus={flashScreenStatus}>
        <>
          {isLocalizationSuccess &&
            localizationData?.data?.length === 0 &&
            showDefaultLangMsg &&
            toast.error(
              "The selected language is not supported. Try selecting a different language.",
              {
                toastId: "",
                onClose: () => setShowDefaultLangMsg(false),
              }
            )}
          {isLocalizationSuccess && transformedData && (
            <>
              <CommonHeader
                setLayoutSelectedAppId={setLayoutSelectedAppId}
                appToToggle={appToToggle}
                appName="Management Portal"
              />

              <div className="admin-dashboard flex">
                <aside className="qi-nav-bar primary left">
                  {/* Fixed top: Management Portal icon */}
                  {filteredClients?.length > 1 && (
                    <div className="qi-nav-bar_top">
                      <div
                        onClick={() => {}}
                        className={`qi-nav-bar_navigation_item active`}
                        key={Math.random()}
                      >
                        <NativeSvgIcon name={"management"} />
                        <div className="qi-nav-bar_navigation_item_info">
                          <span className="">
                            {getLocalizedString("management_protal", "Management Portal")}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Scrollable middle: Menu icons */}
                  <nav className="qi-nav-bar_navigation">
                    <ul className="qi-nav-bar_navigation_sub-nav">
                      {MPEntity.map((element, index) => {
                        if (element?.path) {
                          return (
                            <li className="qi-nav-bar_navigation_sub-nav_item">
                              <NavLink
                                to={`/management_portal/${element.path}`}
                                className={`qi-nav-bar_navigation_sub-nav_item_link ${
                                  element?.subNavigation?.length > 0 ? "" : "visible"
                                }`}
                                key={index}
                              >
                                {!element.path.startsWith("container") && (
                                  <NativeSvgIcon name={`${element.icon}`} />
                                )}
                                {element.path.startsWith("container") && (
                                  <BytesizeIcon name={element.icon} />
                                )}
                                {element?.subNavigation?.length == undefined && (
                                  <div className="qi-nav-bar_navigation_sub-nav_item_info">
                                    <span className="">{element.label}</span>
                                  </div>
                                )}
                              </NavLink>

                              {element?.subNavigation?.length > 0 && (
                                <ul className="qi-nav-bar_navigation_sub-nav_nesting-nav">
                                  {element?.subNavigation?.map((subNav) => (
                                    <li className="qi-nav-bar_navigation_sub-nav_nesting-nav_item">
                                      <NavLink
                                        to={`/management_portal/${subNav.path}`}
                                        className={`qi-nav-bar_navigation_sub-nav_nesting-nav_item_link`}
                                        key={index}
                                      >
                                        <span className="">{subNav.label}</span>
                                      </NavLink>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          );
                        }
                      })}
                    </ul>
                  </nav>

                  {/* Fixed bottom: Tracking & Driver Score icons */}
                  <div className="qi-nav-bar_bottom">
                    {/* Enabled Tracking */}
                    {filteredClients?.length > 1 && (
                      <div
                        onClick={() => {
                          setSwitchClicked("tracking");
                        }}
                        className={`qi-nav-bar_navigation_item`}
                        key={Math.random()}
                      >
                        <NativeSvgIcon name={"tracking"} />
                        <div className="qi-nav-bar_navigation_item_info">
                          <span className="">{getLocalizedString("Tracking", "Tracking")}</span>
                        </div>
                      </div>
                    )}

                    {/* Inactive Driver Score - Only show if user has access to Eco-Drive app */}
                    {filteredClients?.some((app) => app.name?.endsWith("- Eco-Drive")) && (
                      <div
                          onClick={() => {
                            setSwitchClicked("eco-drive");
                          }}
                          className={`qi-nav-bar_navigation_item`}
                          key="driver-score"
                        >
                          <NativeSvgIcon name={"driver-score"} />
                          <div className="qi-nav-bar_navigation_item_info">
                            <span className="">{getLocalizedString("driver_score", "Driver Score")}</span>
                          </div>
                        </div>
                    )}

                    <div className="copyright">
                      <span>&copy;</span>
                      <p className="copyright_info">
                        {getLocalizedString("copy_right", "Copyright")} &copy;
                        {new Date().getFullYear()}{" "}
                        {getLocalizedString(
                          "fleet_management_all_rights_reserved",
                          "Fleet Management. All rights reserved."
                        )}
                      </p>
                    </div>
                  </div>
                </aside>

                <main className="tab-content grow">
                  <Outlet />
                </main>
              </div>
            </>
          )}
          {isLocalizationLoading && (
            <main className="login-layout">
              <section className="text-center happenings">
                <QISpinner />
              </section>
            </main>
          )}
          {localizationError && (
            <div>{getLocalizedString("something_went_wrong", "Something went wrong")}!!</div>
          )}
        </>
      </QIFlashScreen>
      <ToastContainer />
    </>
  );
};
